import { api } from './client'

export interface Advertiser {
  id: string
  user_id: string
  name: string
  email: string
  balance_cents: number
  status: string
  created_at: string
}

export interface Campaign {
  id: string
  advertiser_id: string
  name: string
  status: 'draft' | 'pending_review' | 'approved' | 'rejected' | 'active' | 'paused' | 'completed' | 'cancelled'
  objective: string
  budget_cents: number
  spent_cents: number
  daily_budget_cents: number
  cpm_bid_millicents: number
  target_countries: string[]
  target_languages: string[]
  starts_at?: string
  ends_at?: string
  review_note?: string
  created_at: string
}

export interface CampaignReport {
  campaign_id: string
  impressions: number
  clicks: number
  ctr: number
  spent_cents: number
}

export interface Placement {
  id: string
  channel_id: string
  owner_id: string
  display_name: string
  category: string
  language: string
  country: string
  subscriber_count: number
  status: string
  revenue_share: number
  created_at: string
}

export interface RevenueShare {
  id: string
  placement_id: string
  channel_id: string
  period_date: string
  impressions: number
  gross_cents: number
  share_cents: number
  paid_at?: string
}

export const adsApi = {
  registerAdvertiser: (name: string, email: string): Promise<Advertiser> =>
    api.post('/v1/ads/advertisers', { name, email }),

  getMyAdvertiser: (): Promise<Advertiser> =>
    api.get('/v1/ads/advertisers/me'),

  deposit: (amountCents: number): Promise<{ balance_cents: number }> =>
    api.post('/v1/ads/advertisers/deposit', { amount_cents: amountCents }),

  createCampaign: (data: {
    name: string
    objective: string
    budget_cents: number
    daily_budget_cents?: number
    cpm_bid_millicents?: number
    target_countries?: string[]
  }): Promise<Campaign> =>
    api.post('/v1/ads/campaigns', data),

  submitCampaign: (id: string): Promise<Campaign> =>
    api.post(`/v1/ads/campaigns/${id}/submit`, {}),

  pauseCampaign: (id: string): Promise<void> =>
    api.post(`/v1/ads/campaigns/${id}/pause`, {}),

  getCampaignReport: (id: string): Promise<CampaignReport> =>
    api.get(`/v1/ads/campaigns/${id}/report`),

  registerPlacement: (data: {
    channel_id: string
    display_name: string
    category: string
    language?: string
    country?: string
  }): Promise<Placement> =>
    api.post('/v1/ads/placements', data),

  getMyRevenueShares: (): Promise<{ revenue_shares: RevenueShare[] }> =>
    api.get('/v1/ads/placements/revenue'),

  listAdTransactions: (): Promise<{ transactions: Array<{ id: string; type: string; amount_cents: number; description: string; created_at: string }> }> =>
    api.get('/v1/ads/advertisers/transactions'),

  updateCampaign: (id: string, data: Partial<{ name: string; budget_cents: number; daily_budget_cents: number; target_countries: string[] }>): Promise<Campaign> =>
    api.put(`/v1/ads/campaigns/${id}`, data),

  activateCampaign: (id: string): Promise<Campaign> =>
    api.post(`/v1/ads/campaigns/${id}/activate`, {}),

  createCreative: (campaignId: string, data: {
    title: string
    body: string
    cta_url: string
    cta_label: string
    media_file_id?: string
  }): Promise<{ id: string; title: string; body: string; cta_url: string; cta_label: string; status: string; created_at: string }> =>
    api.post(`/v1/ads/campaigns/${campaignId}/creatives`, data),
}
